import { useNavigate } from "react-router-dom";
import { Button, Panel, Typography, BooleanDisplay } from "@/components/atoms";
import { ConfigDetail } from "@/components/molecules";
import { useAppSelector } from "@/redux/hooks";
import { dispatchSettingsSelector } from "@/redux/slices/business/selectors";
import { DispatchSettingsInfoIcon, renderDispatchTime, renderErrors } from "./helpers";

export const DispatchSettingsDetails = () => {
  const navigate = useNavigate();
  const { errors, details } = useAppSelector(dispatchSettingsSelector);

  const renderRetryCount = (count: string) => {
    if (count) {
      return `${count} time${Number(count) !== 1 ? "s" : ""}`;
    }

    return null;
  };

  const renderFleetDispatchRetryCounts = () => {
    if (Number(details.dispatchAutoOwnFleetRetryCount) > 0 || Number(details.dispatchAutoPrivateNetworkRetryCount) > 0) {
      return (
        <>
          <ConfigDetail
            title="Fleet Dispatch Retry Counts"
            description="When utilising automated dispatch, how many retries before moving on from our own fleet / private network? Note: this may delay speed to offload."
          />
          <ConfigDetail title="Own Fleet - Retry Count" indentPadding="pl-4" isBullet description="Number of times to attempt dispatch">
            {renderRetryCount(details.dispatchAutoOwnFleetRetryCount)}
          </ConfigDetail>
          <ConfigDetail
            title="Private Network - Retry Count"
            indentPadding="pl-4"
            isBullet
            description="Number of times to attempt dispatch"
          >
            {renderRetryCount(details.dispatchAutoPrivateNetworkRetryCount)}
          </ConfigDetail>
        </>
      );
    }
  };

  return (
    <Panel>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Typography variant="h3" className="leading-8">
              Dispatch Settings
            </Typography>
            <DispatchSettingsInfoIcon />
          </div>
          <Typography className="text-neutral-dark-gray">Control the time when you want to dispatch a job for the Drivers.</Typography>
        </div>
        <div className="ml-auto">
          <Button startIcon="Edit2" onClick={() => navigate("edit")}>
            Edit
          </Button>
        </div>
      </div>
      {renderErrors(errors)}

      <ConfigDetail
        title="Hold Jobs Before Dispatch"
        description="Define holding periods before automated dispatch starts after a job being received or edited"
      >
        <BooleanDisplay status={details.enabledHoldBeforeDispatch} />
      </ConfigDetail>
      <ConfigDetail title="Standard Hold Time" description="Time to hold standard jobs" indentPadding="pl-2">
        {renderDispatchTime(details.dispatchHoldBeforeDispatchTime, details.dispatchHoldBeforeDispatchTimeframe)}
      </ConfigDetail>
      <ConfigDetail title="ASAP Hold Time" description="Time to hold ASAP jobs" indentPadding="pl-2">
        {renderDispatchTime(details.dispatchAsapHoldBeforeDispatchTime, details.dispatchAsapHoldBeforeDispatchTimeframe)}
      </ConfigDetail>

      <ConfigDetail title="Automatic dispatch to own fleet" description="Automatic dispatch of jobs to own fleet enabled?">
        <BooleanDisplay status={details.enabledAutomaticDispatch} />
      </ConfigDetail>
      <ConfigDetail title="Dispatch Tier Times" description="Timing of when to dispatch to each tier." indentPadding="pl-2" />
      {/* <ConfigDetail title="Preferred Tier" description="Set when the system will start dispatching before pickup for any jobs  with preferred drivers available" indentPadding="pl-4" isBullet>
        {renderDispatchTime(details.dispatchTier0Time, details.dispatchTier0Timeframe)}
      </ConfigDetail> */}
      <ConfigDetail title="Tier 1" description="Set when the system will start dispatching before pickup" indentPadding="pl-4" isBullet>
        {renderDispatchTime(details.dispatchTier1Time, details.dispatchTier1Timeframe)}
      </ConfigDetail>
      <ConfigDetail title="Tier 2" description="Set when the system will start dispatching before pickup" indentPadding="pl-4" isBullet>
        {renderDispatchTime(details.dispatchTier2Time, details.dispatchTier2Timeframe)}
      </ConfigDetail>
      <ConfigDetail title="Tier 3" description="Set when the system will start dispatching before pickup" indentPadding="pl-4" isBullet>
        {renderDispatchTime(details.dispatchTier3Time, details.dispatchTier3Timeframe)}
      </ConfigDetail>
      <ConfigDetail
        title="Preferred Special Event Tier"
        description="Set when the system will start dispatching before pickup for any jobs  with preferred drivers available"
        indentPadding="pl-4"
        isBullet
      >
        {renderDispatchTime(details.dispatchSpecialEventTier0Time, details.dispatchSpecialEventTier0Timeframe)}
      </ConfigDetail>
      <ConfigDetail
        title="Special Event Tier 1"
        description="Set when the system will start dispatching before pickup"
        indentPadding="pl-4"
        isBullet
      >
        {renderDispatchTime(details.dispatchSpecialEventTier1Time, details.dispatchSpecialEventTier1Timeframe)}
      </ConfigDetail>
      <ConfigDetail
        title="Special Event Tier 2"
        description="Set when the system will start dispatching before pickup"
        indentPadding="pl-4"
        isBullet
      >
        {renderDispatchTime(details.dispatchSpecialEventTier2Time, details.dispatchSpecialEventTier2Timeframe)}
      </ConfigDetail>
      <ConfigDetail
        title="Special Event Tier 3"
        description="Set when the system will start dispatching before pickup"
        indentPadding="pl-4"
        isBullet
      >
        {renderDispatchTime(details.dispatchSpecialEventTier3Time, details.dispatchSpecialEventTier3Timeframe)}
      </ConfigDetail>
      {renderFleetDispatchRetryCounts()}
    </Panel>
  );
};
